const WORKERS = {
  auth:         'https://psicodosis-auth.psicodosis2024.workers.dev',
  inteligencia: 'https://psicodosis-inteligencia.psicodosis2024.workers.dev',
  generacion:   'https://psicodosis-generacion.psicodosis2024.workers.dev',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────
export interface Tema {
  id: string;
  tema: string;
  descripcion: string | null;
  angulo: string | null;
  formato_sugerido: string | null;
  score: number;
  tags: string | null;
  fecha: string;
  usado: number;
  gancho_dm: string | null;
  categoria: string | null;
  razon_viral: string | null;
}

export interface ContenidoItem {
  id: string;
  tipo: string;
  titulo: string;
  cuerpo: unknown;
  formato: string;
  status: 'borrador' | 'aprobado' | 'publicado' | 'rechazado';
  tema_id: string | null;
  created_at: string;
  published_at: string | null;
}

export interface Lead {
  id: string;
  email: string;
  nombre: string | null;
  fuente: string | null;
  instagram_username: string | null;
  created_at: string;
  categoria: string | null;
  flujo_estado: 'completado' | 'parcial' | null;
}

export type PlanTier = 'libre' | 'plus' | 'pro' | 'max';

export interface UsuarioAdmin {
  id: string;
  email: string;
  nombre: string;
  bio: string | null;
  created_at: string;
  plan_tier: PlanTier;
  avatar_url: string | null;
  foto_url: string | null;
  google_id: string | null;
  posts_count: number;
}

export interface AdminUser {
  id: string;
  email: string;
  nombre: string;
  plan: string;
  avatar_url: string | null;
  foto_url: string | null;
}

export interface LoginResult {
  token: string;
  user: AdminUser;
}

export interface PerfilUsuario {
  id: string;
  user_id: string;
  nombre: string | null;
  email: string | null;
  foto_url: string | null;
  bio: string | null;
  created_at: string;
  updated_at: string;
}

export interface Guia {
  id: string;
  categoria: string;
  titulo: string;
  url_r2: string;
  nombre_archivo: string;
  fecha_publicacion: string | null;
  created_at: string;
  activa: number;
}

export interface Material {
  id: string;
  categoria: string;
  tipo: string;
  titulo: string;
  descripcion: string | null;
  url_r2: string;
  nombre_archivo: string;
  created_at: string;
  activo: number;
}

// ─── Core fetch ───────────────────────────────────────────────────────────
async function apiFetch<T>(
  worker: keyof typeof WORKERS,
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('pd_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${WORKERS[worker]}${path}`, { ...init, headers });

  if (res.status === 401) {
    localStorage.removeItem('pd_token');
    localStorage.removeItem('pd_admin_user');
  }

  const data = await res.json() as { ok: boolean; data: T; error?: { message: string } };
  if (!data.ok) throw new Error(data.error?.message ?? 'Error desconocido');
  return data.data;
}

// ─── Auth ─────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    apiFetch<LoginResult>('auth', '/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () =>
    apiFetch<{ user: AdminUser; profile: unknown }>('auth', '/auth/me'),
  getLeads: () =>
    apiFetch<{ leads: Lead[]; total: number }>('auth', '/admin/leads'),
  getUsuarios: () =>
    apiFetch<{ usuarios: UsuarioAdmin[]; total: number }>('auth', '/admin/usuarios'),
};

// ─── Perfil del admin ───────────────────────────────────────────────────────
export const perfilApi = {
  getPerfil: () =>
    apiFetch<{ perfil: PerfilUsuario | null }>('auth', '/perfil'),
  updatePerfil: (data: { nombre?: string; bio?: string }) =>
    apiFetch<{ perfil: PerfilUsuario }>('auth', '/perfil', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  subirFoto: (data: { foto_base64: string; mime_type: string }) =>
    apiFetch<{ foto_url: string }>('auth', '/perfil/foto', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  cambiarPassword: (data: { password_actual: string; password_nueva: string }) =>
    apiFetch<{ changed: boolean }>('auth', '/auth/cambiar-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ─── Inteligencia ─────────────────────────────────────────────────────────
export const inteligenciaApi = {
  getTemas: () =>
    apiFetch<{ temas: Tema[]; total: number }>('inteligencia', '/temas'),
  generarTemas: (params?: { categoria?: string }) =>
    apiFetch<{ insertados: number; temas: Tema[]; categoria: string }>('inteligencia', '/temas/generar', {
      method: 'POST',
      body: JSON.stringify(params ?? {}),
    }),
};

// ─── Guías ────────────────────────────────────────────────────────────────
export const guiasApi = {
  getGuias: (params?: { categoria?: string }) => {
    const qs = params?.categoria ? `?categoria=${encodeURIComponent(params.categoria)}` : '';
    return apiFetch<{ guias: Guia[]; total: number }>('generacion', `/guias${qs}`);
  },
  uploadGuia: (data: { categoria: string; titulo: string; pdf_base64: string; nombre_archivo: string }) =>
    apiFetch<Guia>('generacion', '/guias/upload', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteGuia: (id: string) =>
    apiFetch<{ id: string; deleted: boolean }>('generacion', `/guias/${id}`, {
      method: 'DELETE',
    }),
};

// ─── Material ─────────────────────────────────────────────────────────────
export const materialApi = {
  getMaterial: (params?: { categoria?: string }) => {
    const qs = params?.categoria ? `?categoria=${encodeURIComponent(params.categoria)}` : '';
    return apiFetch<{ material: Material[]; total: number }>('generacion', `/material${qs}`);
  },
  uploadMaterial: (data: {
    categoria: string;
    tipo: string;
    titulo: string;
    descripcion?: string;
    archivo_base64: string;
    nombre_archivo: string;
  }) =>
    apiFetch<Material>('generacion', '/material', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteMaterial: (id: string) =>
    apiFetch<{ id: string; deleted: boolean }>('generacion', `/material/${id}`, {
      method: 'DELETE',
    }),
};

// ─── Foro admin ───────────────────────────────────────────────────────────
export interface ForoTemaAdmin {
  id: string;
  titulo: string;
  descripcion: string | null;
  categoria: string;
  creado_por: string;
  activo: number;
  created_at: string;
  post_count?: number;
  ultima_actividad?: string;
}

export interface ForoPostAdmin {
  id: string;
  tema_id: string;
  parent_id: string | null;
  user_id: string;
  nombre_usuario: string;
  contenido: string;
  likes: number;
  created_at: string;
}

export const foroAdminApi = {
  getTemas: (params?: { categoria?: string }) => {
    const qs = params?.categoria ? `?categoria=${encodeURIComponent(params.categoria)}` : '';
    return apiFetch<{ temas: ForoTemaAdmin[]; total: number }>('auth', `/foro/temas${qs}`);
  },
  createTema: (data: { titulo: string; descripcion?: string; categoria: string }) =>
    apiFetch<{ tema: ForoTemaAdmin }>('auth', '/foro/temas', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateTema: (id: string, data: { titulo?: string; descripcion?: string; categoria?: string }) =>
    apiFetch<{ tema: ForoTemaAdmin }>('auth', `/foro/temas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteTema: (id: string) =>
    apiFetch<{ id: string; desactivado: boolean }>('auth', `/foro/temas/${id}`, { method: 'DELETE' }),
  getPosts: (tema_id: string) =>
    apiFetch<{ posts: ForoPostAdmin[]; total: number }>('auth', `/foro/posts/${tema_id}`),
  deletePost: (id: string) =>
    apiFetch<{ id: string; deleted: boolean }>('auth', `/foro/posts/${id}`, { method: 'DELETE' }),
};

// ─── Generacion ───────────────────────────────────────────────────────────
export const generacionApi = {
  getContenido: (params?: { tipo?: string; status?: string; tema_id?: string }) => {
    const entries = Object.entries(params ?? {}).filter(([, v]) => v) as [string, string][];
    const qs = entries.length ? '?' + new URLSearchParams(entries).toString() : '';
    return apiFetch<{ items: ContenidoItem[]; total: number }>('generacion', `/contenido${qs}`);
  },
  generarTodo: (tema: Tema) =>
    apiFetch<{ generados: number; piezas: Array<{ id: string; tipo: string; titulo: string; contenido: unknown }> }>(
      'generacion',
      '/generacion/todo',
      {
        method: 'POST',
        body: JSON.stringify({
          tema_id: tema.id,
          titulo: tema.tema,
          categoria: tema.categoria,
          gancho_dm: tema.gancho_dm,
        }),
      }
    ),
  updateStatus: (id: string, status: string) =>
    apiFetch<{ id: string; status: string }>('generacion', `/contenido/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  getHistorial: () =>
    apiFetch<{ items: ContenidoItem[]; total: number }>('generacion', '/contenido?status=publicado'),
};
